import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';

import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

const AgreeToChargeCard = ({
	errors,
	formData,
	handleInputChange,
	membershipListData
}) => {
	const { t } = useTranslation('agreeToChargeCard');
	const { result } = useSelector(
		(state) => state.customerDetailsSlice.postBrowserDetails
	);

	const includesAny = (mainArray, searchValues) => {
		return searchValues.some((value) => mainArray.includes(value));
	};

	const handleOfferDetailsChange = (e) => {
		handleInputChange('offerDetailsChecked', e.target.checked);
	};

	const handleTermsPrivacyChange = (e) => {
		const isChecked = e.target.checked;
		handleInputChange('privacyChecked', isChecked);
		handleInputChange('termsChecked', isChecked);
	};

	const handleMarketingChange = (e) => {
		handleInputChange('marketingChecked', e.target.checked);
	};

	const hasOfferError = () => {
		const errorsExist = Object.keys(errors);
		return (
			errorsExist.length > 0 &&
			includesAny(errorsExist, ['offerDetailsChecked'])
		);
	};

	const hasTermsError = () => {
		const errorsExist = Object.keys(errors);
		return (
			errorsExist.length > 0 &&
			includesAny(errorsExist, ['privacyChecked', 'termsChecked'])
		);
	};

	const hasMarketingError = () => {
		const errorsExist = Object.keys(errors);
		return (
			errorsExist.length > 0 && includesAny(errorsExist, ['marketingChecked'])
		);
	};

	const offerDetailsLabel = (
		<Typography variant='body2' component='span'>
			{t('offerDetailsText', {
				trialPrice: result.trial_price,
				trialDuration: result.trial_period,
				price: result.price,
				billingDuration: result.billing_duration
			})}
		</Typography>
	);

	const termsLabel = (
		<Typography variant='body2' component='span'>
			<Trans
				i18nKey='agreeToChargeCard:acceptPolicy'
				values={{ membership: membershipListData.name }}
				components={{
					termsLink: (
						<Link
							href={result.terms_and_conditions_link}
							target='_blank'
							rel='noreferrer'
							sx={{ color: 'primary.main' }}
						/>
					),
					privacyLink: (
						<Link
							href={result.privacy_policy_link}
							target='_blank'
							rel='noreferrer'
							sx={{ color: 'primary.main' }}
						/>
					)
				}}
			/>
		</Typography>
	);

	const marketingLabel = (
		<Typography variant='body2' component='span'>
			{t('marketingConsent', { membership: membershipListData.name })}
		</Typography>
	);

	return (
		<Stack spacing={1}>
			<FormControlLabel
				control={
					<Checkbox
						checked={formData.offerDetailsChecked}
						onChange={handleOfferDetailsChange}
						size='small'
						sx={{
							color: hasOfferError() ? 'error.main' : 'inherit',
							'&.Mui-checked': {
								color: hasOfferError() ? 'error.main' : 'primary.main'
							}
						}}
					/>
				}
				label={offerDetailsLabel}
				sx={{
					alignItems: 'flex-start',
					'& .MuiFormControlLabel-label': {
						pt: 0.75
					}
				}}
			/>
			{hasOfferError() && (
				<Typography
					variant='caption'
					sx={{ color: 'error.main', pl: 4, mt: -0.5 }}
				>
					{t('acceptOfferError')}
				</Typography>
			)}

			<FormControlLabel
				control={
					<Checkbox
						checked={formData.privacyChecked}
						onChange={handleTermsPrivacyChange}
						size='small'
						sx={{
							color: hasTermsError() ? 'error.main' : 'inherit',
							'&.Mui-checked': {
								color: hasTermsError() ? 'error.main' : 'primary.main'
							}
						}}
					/>
				}
				label={termsLabel}
				sx={{
					alignItems: 'flex-start',
					'& .MuiFormControlLabel-label': {
						pt: 0.75
					}
				}}
			/>
			{hasTermsError() && (
				<Typography
					variant='caption'
					sx={{ color: 'error.main', pl: 4, mt: -0.5 }}
				>
					{t('acceptPolicyError')}
				</Typography>
			)}

			<FormControlLabel
				control={
					<Checkbox
						checked={formData.marketingChecked || false}
						onChange={handleMarketingChange}
						size='small'
						sx={{
							color: hasMarketingError() ? 'error.main' : 'inherit',
							'&.Mui-checked': {
								color: hasMarketingError() ? 'error.main' : 'primary.main'
							}
						}}
					/>
				}
				label={marketingLabel}
				sx={{
					alignItems: 'flex-start',
					'& .MuiFormControlLabel-label': {
						pt: 0.75
					}
				}}
			/>
			{hasMarketingError() && (
				<Typography
					variant='caption'
					sx={{ color: 'error.main', pl: 4, mt: -0.5 }}
				>
					{t('acceptMarketingError')}
				</Typography>
			)}
		</Stack>
	);
};

AgreeToChargeCard.propTypes = {
	errors: PropTypes.object,
	formData: PropTypes.object,
	handleInputChange: PropTypes.func,
	membershipListData: PropTypes.object
};

export default AgreeToChargeCard;
